import { computed, Injectable, signal } from '@angular/core';

import environment from '../../environments/environment';
import { STREAMING_RESPONSE_MESSAGES } from '../shared/constants/status-responses.constant';

@Injectable({
    providedIn: 'root',
})
export class ChatMessageProgressService {
    private readonly _progress = signal<number>(0);
    readonly progress = this._progress.asReadonly();

    private intervalId?: number;
    private elapsedSeconds = 0;
    private slowPhaseStarted = false;

    private readonly _currentStatus = signal<string>('');
    private readonly streamingResponseMessages = this.parseStreamingResponseMessages(
        environment.STREAMING_RESPONSE_MESSAGES
    );
    readonly currentStatus = computed(() => {
        if (this.streamingResponseMessages.length === 0) {
            return 'Processing';
        }
        if (this._progress() < 33) {
            return this.streamingResponseMessages[0];
        }
        if (this._progress() < 66) {
            return this.streamingResponseMessages[1];
        }
        if (this._progress() <= 100) {
            return this.streamingResponseMessages[2];
        }
        return 'Processing';
    });

    /**
     *
     */
    get progressValue(): number {
        return this._progress();
    }

    /**
     *
     */
    get currentStatusValue(): string {
        return this.currentStatus();
    }

    /**
     *
     */
    start(): void {
        this.reset();

        this.intervalId = window.setInterval(() => {
            this.elapsedSeconds++;

            if (this.elapsedSeconds <= 15) {
                const value = Math.min(Math.round((this.elapsedSeconds / 15) * 95), 95);
                this._progress.set(value);
                return;
            }

            if (!this.slowPhaseStarted) {
                this.slowPhaseStarted = true;
            }

            if (this.elapsedSeconds % 5 === 0) {
                const current = this._progress();
                if (current < 99) {
                    this._progress.set(current + 1);
                }
            }
        }, 1000);
    }

    /**
     *
     */
    complete(): void {
        this.clearTimer();
        this._progress.set(100);
        setTimeout(() => {
            this._progress.set(0);
        }, 1000);
    }

    /**
     *
     */
    reset(): void {
        this.clearTimer();
        this.elapsedSeconds = 0;
        this.slowPhaseStarted = false;
    }

    /**
     *
     */
    private clearTimer(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
            this._progress.set(0);
        }
    }

    /**
     *
     * @param value
     */
    private parseStreamingResponseMessages(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value.map((item) => String(item).trim()).filter(Boolean);
        }

        if (typeof value !== 'string') {
            return STREAMING_RESPONSE_MESSAGES;
        }

        const normalizedValue = value.trim();
        if (!normalizedValue) {
            return STREAMING_RESPONSE_MESSAGES;
        }

        try {
            const jsonLike = normalizedValue.replace(/'/g, '"');
            const parsed = JSON.parse(jsonLike);

            if (Array.isArray(parsed)) {
                const messages = parsed.map((item) => String(item).trim()).filter(Boolean);
                return messages.length ? messages : STREAMING_RESPONSE_MESSAGES;
            }
        } catch {
            console.error('Error parsing streaming response messages:', value);
            return STREAMING_RESPONSE_MESSAGES;
        }

        const messages = normalizedValue
            .replace(/^\[|\]$/g, '')
            .split(',')
            .map((item) => item.trim().replace(/^['"]|['"]$/g, ''))
            .filter(Boolean);

        return messages.length ? messages : STREAMING_RESPONSE_MESSAGES;
    }

    /**
     *
     * @param value
     */
    setProgress(value: number): void {
        this._progress.set(value);
    }
}
